import { Link } from 'react-router-dom'
import { useCallback } from 'react'

import { OrderType, OwnersResponseType, OwnersSortByType } from '@/shared/backend/types/leaderboard'

import { HeaderContainer, IconNextContainer, RowWrapper, TableContainer, Td, Th } from '../components/Table'
import { ellipsizeTokenAddress } from 'utils/helpers'
import { IconButtonNext } from 'components/Icons/IconButtonNext'
import Pagination from 'components/Pagination/Pagination'
import { IconArrowUp } from 'components/Icons/IconArrowUp'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { getPctValue } from 'utils/pctFormatter'
import { TableContentLoader } from '../components/TableContentLoader'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { OwnersResponseTypeWithRank } from '../hooks/useLeaderboardOwners'

interface Props {
  sortBy: OwnersSortByType
  handleColumnClick: (columnKey: OwnersSortByType) => void
  sortOrder: OrderType
  paginatedItems: OwnersResponseTypeWithRank[]
  currentPage: number
  totalPages: number
  handlePageChange: (page: number) => void
  isLoading: boolean
}

export const WalletsTable = ({
  sortBy,
  handleColumnClick,
  sortOrder,
  paginatedItems,
  currentPage,
  totalPages,
  handlePageChange,
  isLoading,
}: Props) => {
  const getSortIndicator = useCallback(
    (key: OwnersSortByType) => {
      if (sortBy === key) {
        return sortOrder === 'asc' ? <IconArrowUp /> : <IconArrowDown />
      }
      return null
    },
    [sortBy, sortOrder]
  )

  return (
    <TableContainer>
      <HeaderContainer style={{ position: 'relative' }}>
        <Th variant="description" style={{ flexBasis: 100, cursor: 'auto' }} $disabled={isLoading}>
          #
        </Th>
        <Th variant="description" style={{ flexBasis: 200, cursor: 'auto' }} $disabled={isLoading}>
          Wallet
        </Th>
        <Tooltip
          position="top"
          content="Equity across all open positions."
          tooltipBoxStyles={{ maxWidth: 250, minWidth: 250 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 200 }}
        >
          <Th
            variant="description"
            fontWeight={sortBy === 'openEquity' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 200 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('openEquity')}>Open equity {getSortIndicator('openEquity')}</div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Realized PnL from closed positions."
          tooltipBoxStyles={{ maxWidth: 270, minWidth: 270 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 200 }}
        >
          <Th
            variant="description"
            fontWeight={sortBy === 'totalPnl' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 200 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('totalPnl')}>PnL {getSortIndicator('totalPnl')}</div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Cumulative PnL as % of equity (closed positions)."
          tooltipBoxStyles={{ maxWidth: 370, minWidth: 370 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 200 }}
        >
          <Th
            variant="description"
            fontWeight={sortBy === 'totalReturnOnEquity' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 200 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('totalReturnOnEquity')}>
              ROI {getSortIndicator('totalReturnOnEquity')}
            </div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Benchmark: gain/loss vs. holding tokens (closed positions)."
          tooltipBoxStyles={{ maxWidth: 440, minWidth: 440 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 200 }}
        >
          <Th
            variant="description"
            fontWeight={sortBy === 'totalHodlBenchmarkPnl' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 200 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('totalHodlBenchmarkPnl')}>
              HODL {getSortIndicator('totalHodlBenchmarkPnl')}
            </div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Count of closed positions."
          tooltipBoxStyles={{ maxWidth: 200, minWidth: 200 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 200 }}
        >
          <Th
            variant="description"
            fontWeight={sortBy === 'totalPositions' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 200 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('totalPositions')}>
              Positions {getSortIndicator('totalPositions')}
            </div>
          </Th>
        </Tooltip>
      </HeaderContainer>
      {isLoading ? (
        <TableContentLoader />
      ) : (
        <div>
          {paginatedItems.map(d => (
            <Link
              key={d.ownerId}
              to={`https://suivision.xyz/account/${d.ownerId}`}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', pointerEvents: 'auto' }}
            >
              <RowWrapper>
                <Td variant="description" style={{ flexBasis: 100 }}>
                  {d.rank}
                </Td>
                <Td variant="description" style={{ flexBasis: 200 }}>
                  {ellipsizeTokenAddress(d.ownerId)}
                </Td>
                <Td variant="description" style={{ flexBasis: 200 }}>
                  {getFormattedUsdValue(d.openEquity)}
                </Td>
                <Td variant="description" style={{ flexBasis: 200 }}>
                  {typeof d.totalPnl !== 'number' ? '-' : getFormattedUsdValue(d.totalPnl)}
                </Td>
                <Td variant="description" style={{ flexBasis: 200 }}>
                  {typeof d.totalReturnOnEquity !== 'number' ? '-' : getPctValue(d.totalReturnOnEquity)}
                </Td>
                <Td variant="description" style={{ flexBasis: 200 }}>
                  {typeof d.totalHodlBenchmarkPnl !== 'number' ? '-' : getFormattedUsdValue(d.totalHodlBenchmarkPnl)}
                </Td>
                <Td variant="description" style={{ flexBasis: 200 }}>
                  {d.totalPositions}
                </Td>
                <IconNextContainer>
                  <IconButtonNext />
                </IconNextContainer>
              </RowWrapper>
            </Link>
          ))}
        </div>
      )}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        type="leaderboard-wallets"
      />
    </TableContainer>
  )
}
